import React, { useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, Row, Col, Spinner, Card, CardHeader, CardBody, Form } from 'reactstrap'
import McDataServices from '../../services/McDataServices'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { RSelect } from '../../components/Component'

const UserSubscriptionModal = ({ isOpen, toggle, selectedUser }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hsOptions, setHsOptions] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [hsInput, setHsInput] = useState('')
  const [b2bHsInput, setB2bHsInput] = useState('')

  const userId = selectedUser?.id
  const userName = selectedUser?.userName

  const [formData, setFormData] = useState({
    packageName: '',
    startDate: '',
    expiryDate: '',
    ruleMap: {},
    mailingUserSubscription: {
      packageName: '',
      startDate: '',
      expiryDate: '',
      ruleMap: {}
    }
  })

  useEffect(() => {
    if (isOpen && userId) {
      fetchSubscription()
      fetchOptions()
    } else {
      setFormData({
        packageName: '',
        startDate: '',
        expiryDate: '',
        ruleMap: {},
        mailingUserSubscription: {
          packageName: '',
          startDate: '',
          expiryDate: '',
          ruleMap: {}
        }
      })
      setHsInput('')
      setB2bHsInput('')
    }
  }, [isOpen, userId])

  const fetchOptions = async () => {
    try {
      const hsResult = await McDataServices.getHsCodes()
      setHsOptions(hsResult || [])

      const countryResult = await McDataServices.getCountries()
      if (countryResult?.search_countries) {
        const sortedCountries = countryResult.search_countries
          .map(c => ({
            value: c.countryCode,
            label: c.label
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        setCountryOptions(sortedCountries)
      }
    } catch (error) {
      console.error('Failed to fetch options', error)
    }
  }

  const fetchSubscription = async () => {
    setLoading(true)
    try {
      const data = await McDataServices.getUserSubscription(userId)
      if (data) {
        setFormData({
          packageName: data.packageName || '',
          startDate: data.startDate || '',
          expiryDate: data.expiryDate || '',
          ruleMap: data.ruleMap || {},
          mailingUserSubscription: data.mailingUserSubscription || {
            packageName: '',
            startDate: '',
            expiryDate: '',
            ruleMap: {}
          }
        })
      }
    } catch (error) {
      toast.error(t('admin.modal.fetchError', 'Failed to fetch subscription details.'), {
        position: 'top-center',
        autoClose: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const { packageName, ...restFormData } = formData
      const { packageName: mailingPackageName, ...restMailingUserSubscription } = restFormData.mailingUserSubscription || {}

      const payload = {
        userId,
        ...restFormData,
        mailingUserSubscription: Object.keys(restMailingUserSubscription).length > 0 ? restMailingUserSubscription : null
      }
      await McDataServices.updateUserSubscription(payload)
      toast.success(t('admin.modal.updateSuccess', 'User subscription updated successfully.'), {
        position: 'top-center',
        autoClose: 3000
      })
      toggle()
    } catch (error) {
      toast.error(error?.detail || t('admin.modal.updateError', 'Failed to update subscription.'), {
        position: 'top-center',
        autoClose: 3000
      })
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleRuleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      ruleMap: {
        ...prev.ruleMap,
        [key]: value
      }
    }))
  }

  const handleMailingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      mailingUserSubscription: {
        ...prev.mailingUserSubscription,
        [field]: value
      }
    }))
  }

  const handleMailingRuleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      mailingUserSubscription: {
        ...prev.mailingUserSubscription,
        ruleMap: {
          ...prev.mailingUserSubscription?.ruleMap,
          [key]: value
        }
      }
    }))
  }

  const renderRuleMapInputs = (rules, onChangeHandler, priorityKeys = []) => {
    const ruleEntries = Object.entries(rules)
    const priorityItems = ruleEntries.filter(([key]) => priorityKeys.includes(key))
    const otherItems = ruleEntries.filter(([key]) => !priorityKeys.includes(key))

    const renderItem = ([key, val], isPriority) => {
      const translatedKey = t(`payment.rules.${key}`, key)
      const isCountrySet = key.includes('SEARCH_COUNTRY_SET')
      const isHsSet = key.includes('HS_CODE_SET')

      if (isCountrySet || isHsSet) {
        const isB2B = key.startsWith('B2B_')
        const currentInput = isHsSet ? (isB2B ? b2bHsInput : hsInput) : ''
        const showOptions = !isHsSet || currentInput.length >= 3

        const options = isCountrySet ? countryOptions : (showOptions ? hsOptions : [])
        const currentValues = val
          ? val.toString().split(',').map(v => {
            const found = options.find(o => o.value === v.trim()) || hsOptions.find(o => o.value === v.trim())
            return found || { value: v.trim(), label: v.trim() }
          }).filter(Boolean)
          : []

        return (
          <Col xl={isPriority ? '6' : '12'} md="12" key={key} className="mb-2">
            <FormGroup className="mb-0">
              <Label className="text-muted font-weight-bold mb-0" style={{ fontSize: '0.7rem' }}>{translatedKey}</Label>
              <RSelect
                isMulti
                options={options}
                value={currentValues}
                onInputChange={(val) => {
                  if (isHsSet) {
                    if (isB2B) setB2bHsInput(val)
                    else setHsInput(val)
                  }
                }}
                noOptionsMessage={() => !showOptions ? t('min_3_char', 'Please enter at least 3 characters') : t('no_option', 'No options')}
                formatOptionLabel={(opt, { context }) => {
                  if (isHsSet) {
                    if (context === 'value') return opt.value
                    return (
                      <div>
                        <div className="fw-bold" style={{ fontSize: '0.85rem' }}>{opt.label}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {t('hs_code', 'GTIP Code')}: {opt.value}
                        </div>
                      </div>
                    )
                  }
                  return opt.label
                }}
                menuPortalTarget={document.body}
                styles={{
                  menu: (base) => ({
                    ...base,
                    width: '100%',
                    minWidth: '100%',
                    zIndex: 9999
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
                onChange={(selected) => {
                  const joinValue = selected ? selected.map(s => s.value).join(',') : ''
                  onChangeHandler(key, joinValue)
                }}
                className="react-select-container-sm"
              />
            </FormGroup>
          </Col>
        )
      }

      const type = typeof val === 'number' || (val !== '' && !isNaN(val)) ? 'number' : 'text'
      return (
        <Col xl="3" md="4" sm="6" key={key} className="mb-2">
          <FormGroup className="mb-0">
            <Label className="text-muted font-weight-bold mb-0" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                   title={translatedKey}>{translatedKey}</Label>
            <Input
              bsSize="sm"
              type={type}
              value={val}
              onChange={(e) => onChangeHandler(key, type === 'number' ? Number(e.target.value) : e.target.value)}
              className="px-2 py-1"
              style={{ fontSize: '0.8rem', height: 'auto' }}
            />
          </FormGroup>
        </Col>
      )
    }

    return (
      <>
        {priorityItems.map(item => renderItem(item, true))}
        {otherItems.map(item => renderItem(item, false))}
      </>
    )
  }

  return (
    <>
      <style>
        {`
          .super-wide-modal {
            max-width: 95vw !important;
            width: 95vw !important;
          }
          .compact-card-body {
             padding: 0.75rem !important;
          }
          .compact-card-header {
             padding: 0.5rem 0.75rem !important;
          }
        `}
      </style>
      <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered super-wide-modal" size="xl">
        <ModalHeader toggle={toggle} className="bg-light border-bottom py-2">
          <h6 className="mb-0">{t('admin.modal.editSubscription', 'Edit Subscription')}: {userName || userId}</h6>
        </ModalHeader>
        <ModalBody className="p-3">
          <Card className="mb-3 shadow-sm border-0 border-start border-primary border-4 bg-light">
            <CardBody className="compact-card-body d-flex align-items-center flex-wrap" style={{ fontSize: '0.85rem' }}>
              <div className="me-4 text-truncate">
                <strong>{t('admin.table.username', 'Username')}:</strong> <span className="text-muted">{selectedUser?.userName || '-'}</span>
              </div>
              <div className="me-4 text-truncate">
                <strong>{t('admin.table.email', 'Email')}:</strong> <span className="text-muted">{selectedUser?.email || '-'}</span>
              </div>
              <div className="me-4 text-truncate">
                <strong>{t('admin.table.nameSurname', 'Name Surname')}:</strong> <span className="text-muted">{selectedUser?.nameSurname || '-'}</span>
              </div>
              <div className="me-4 text-truncate">
                <strong>{t('admin.table.companyName', 'Company Name')}:</strong> <span className="text-muted">{selectedUser?.companyName || '-'}</span>
              </div>
              <div className="me-4 text-truncate">
                <strong>{t('admin.table.countryCode', 'Country')}:</strong> <span className="text-muted">{selectedUser?.countryCode || '-'}</span>
              </div>
              <div className="me-4 text-truncate">
                <strong>{t('admin.table.city', 'City')}:</strong> <span className="text-muted">{selectedUser?.city || '-'}</span>
              </div>
              <div className="me-4 text-truncate">
                <strong>{t('admin.table.tel', 'Tel')}:</strong> <span className="text-muted">{selectedUser?.tel || '-'}</span>
              </div>
            </CardBody>
          </Card>

          {loading
            ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner color="primary"/>
              </div>
              )
            : (
              <Form onSubmit={handleSave}>
                <Row>
                  <Col lg="6">
                    <Card className="mb-0 shadow-sm border-0 bg-light">
                      <CardHeader className="bg-transparent border-bottom compact-card-header">
                        <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{t('admin.modal.generalSubscription', 'General Subscription')}</h6>
                      </CardHeader>
                      <CardBody className="compact-card-body">
                        <Row className="mb-2">
                          <Col md="4">
                            <FormGroup className="mb-1">
                              <Label className="mb-0" style={{ fontSize: '0.75rem' }}>{t('admin.modal.packageName', 'Package Name')}</Label>
                              <div className="pt-1 text-dark" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                                {formData.packageName || '-'}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md="4">
                            <FormGroup className="mb-1">
                              <Label className="mb-0" style={{ fontSize: '0.75rem' }}>{t('admin.modal.startDate', 'Start Date')}</Label>
                              <Input
                                bsSize="sm"
                                type="date"
                                value={formData.startDate}
                                onChange={(e) => handleChange('startDate', e.target.value)}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="4">
                            <FormGroup className="mb-1">
                              <Label className="mb-0" style={{ fontSize: '0.75rem' }}>{t('admin.modal.expiryDate', 'Expiry Date')}</Label>
                              <Input
                                bsSize="sm"
                                type="date"
                                value={formData.expiryDate}
                                onChange={(e) => handleChange('expiryDate', e.target.value)}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <hr className="my-2"/>
                        <Row>
                          {formData.ruleMap && Object.keys(formData.ruleMap).length > 0
                            ? (
                                renderRuleMapInputs(formData.ruleMap, handleRuleChange, ['HS_CODE_SET', 'SEARCH_COUNTRY_SET'])
                              )
                            : (
                              <Col><p className="text-muted small mb-0">{t('admin.modal.noRules', 'No rules defined.')}</p></Col>
                              )}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="6">
                    <Card className="mb-0 shadow-sm border-0 bg-light">
                      <CardHeader className="bg-transparent border-bottom compact-card-header">
                        <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{t('admin.modal.mailingSubscription', 'Mailing Subscription')}</h6>
                      </CardHeader>
                      <CardBody className="compact-card-body">
                        <Row className="mb-2">
                          <Col md="4">
                            <FormGroup className="mb-1">
                              <Label className="mb-0" style={{ fontSize: '0.75rem' }}>{t('admin.modal.packageName', 'Package Name')}</Label>
                              <div className="pt-1 text-dark" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                                {formData.mailingUserSubscription?.packageName || '-'}
                              </div>
                            </FormGroup>
                          </Col>
                          {formData.mailingUserSubscription?.ruleMap && Object.keys(formData.mailingUserSubscription.ruleMap).length > 0 && (
                            <>
                              <Col md="4">
                                <FormGroup className="mb-1">
                                  <Label className="mb-0" style={{ fontSize: '0.75rem' }}>{t('admin.modal.startDate', 'Start Date')}</Label>
                                  <Input
                                    bsSize="sm"
                                    type="date"
                                    value={formData.mailingUserSubscription?.startDate || ''}
                                    onChange={(e) => handleMailingChange('startDate', e.target.value)}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="4">
                                <FormGroup className="mb-1">
                                  <Label className="mb-0" style={{ fontSize: '0.75rem' }}>{t('admin.modal.expiryDate', 'Expiry Date')}</Label>
                                  <Input
                                    bsSize="sm"
                                    type="date"
                                    value={formData.mailingUserSubscription?.expiryDate || ''}
                                    onChange={(e) => handleMailingChange('expiryDate', e.target.value)}
                                  />
                                </FormGroup>
                              </Col>
                            </>
                          )}
                        </Row>
                        <hr className="my-2"/>
                        <Row>
                          {formData.mailingUserSubscription?.ruleMap && Object.keys(formData.mailingUserSubscription.ruleMap).length > 0
                            ? (
                                renderRuleMapInputs(formData.mailingUserSubscription.ruleMap, handleMailingRuleChange, ['B2B_SEARCH_COUNTRY_SET', 'B2B_HS_CODE_SET'])
                              )
                            : (
                              <Col><p className="text-muted small mb-0">{t('admin.modal.noMailingRules', 'No mailing rules defined.')}</p></Col>
                              )}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Form>
              )}
        </ModalBody>
        <ModalFooter className="bg-light border-top py-2">
          <Button color="secondary" size="sm" onClick={toggle} disabled={saving} className="px-4">
            {t('admin.modal.cancel', 'Cancel')}
          </Button>
          <Button color="primary" size="sm" onClick={handleSave} disabled={loading || saving} className="px-4">
            {saving ? <Spinner size="sm"/> : t('admin.modal.saveChanges', 'Save Changes')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default UserSubscriptionModal
